import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';
import util from 'preact-util';

configure({ enforceActions: 'always' });

class ExternalRaceResultStore extends LocalModel {
    constructor() {
        super('externalRaceResult', {
            namePlural: 'externalRaceResults',
            namePluralReal: 'externalraceresults',
            sort: '-raceDate',
            limit: 50,
            api: {
                search: {
                    url: '/api/externalraceresults/',
                    params: {
                        limit: 50,
                    },
                },
                load: {
                    url: '/api/externalraceresults/',
                    params: {},
                },
                save: {
                    url: '/api/externalraceresults/',
                    params: {},
                },
                delete: {
                    url: '/api/externalraceresults/',
                    params: {},
                },
            },
        });
    }

    @observable externalRaceResults = [];

    @observable externalRaceResult = {};

    @observable isLoading = false;

    @observable uploadResult = null;

    @observable years = [];

    @observable selectedYear = null;

    @observable totalResults = 0;

    @action
    setIsLoading = (loading) => {
        this.isLoading = loading;
    }

    @action
    setUploadResult = (result) => {
        this.uploadResult = result;
    }

    @action
    setYears = (years) => {
        this.years = years;
    }

    @action
    setSelectedYear = (year) => {
        this.selectedYear = year;
    }

    @action
    setTotalResults = (total) => {
        this.totalResults = total;
    }

    /**
     * Load distinct years from external race results
     */
    loadYears = async () => {
        try {
            const response = await util.fetchApi('/api/externalraceresults/years', { method: 'GET' });
            if (response.status === 200) {
                this.setYears(response.data || []);
                return response.data;
            }
            return [];
        } catch (error) {
            console.error('Error loading years:', error);
            return [];
        }
    }

    /**
     * Load list of external race results
     */
    loadExternalRaceResults = async (params = {}) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi('/api/externalraceresults/', { method: 'GET' }, params);
            if (response.status === 200) {
                this.updateKeyValue('externalRaceResults', response.data || []);
                if (response.meta && response.meta.total !== undefined) {
                    this.setTotalResults(response.meta.total);
                }
                return response.data;
            }
            return [];
        } catch (error) {
            console.error('Error loading external race results:', error);
            return [];
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Load single external race result by ID
     */
    loadExternalRaceResult = async (id) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi(`/api/externalraceresults/${id}`, { method: 'GET' });
            if (response.status === 200) {
                this.updateKeyValue('externalRaceResult', response.data || {});
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error loading external race result:', error);
            return null;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Create a new external race result
     */
    createExternalRaceResult = async (data) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi('/api/externalraceresults/', { method: 'POST' }, data);
            if (response.status === 201 || response.status === 200) {
                return response.data;
            }
            throw new Error(response.message || 'Failed to create external race result');
        } catch (error) {
            console.error('Error creating external race result:', error);
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Update an external race result
     */
    updateExternalRaceResult = async (id, data) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi(`/api/externalraceresults/${id}`, { method: 'PATCH' }, data);
            if (response.status === 200) {
                return response.data;
            }
            throw new Error(response.message || 'Failed to update external race result');
        } catch (error) {
            console.error('Error updating external race result:', error);
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Delete an external race result
     * @param {number} id - The ID of the external race result to delete
     * @param {boolean} removeRatings - If true, also remove associated musher ratings
     */
    deleteExternalRaceResult = async (id, removeRatings = false) => {
        this.setIsLoading(true);
        try {
            const params = removeRatings ? { removeRatings: true } : {};
            const response = await util.fetchApi(`/api/externalraceresults/${id}`, { method: 'DELETE' }, params);
            if (response.status === 200) {
                return response.data;
            }
            throw new Error(response.message || 'Failed to delete external race result');
        } catch (error) {
            console.error('Error deleting external race result:', error);
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Upload CSV and create external race result
     */
    uploadCSV = async ({ raceName, raceClassName, raceDate, distance, location, country, csvContent, notes }) => {
        this.setIsLoading(true);
        this.setUploadResult(null);
        try {
            const response = await util.fetchApi('/api/externalraceresults/csv', { method: 'POST' }, {
                raceName,
                raceClassName,
                raceDate,
                distance,
                location,
                country,
                csvContent,
                notes,
            });
            if (response.status === 201 || response.status === 200) {
                this.setUploadResult({
                    success: true,
                    data: response.data,
                    meta: response.meta,
                });
                return response;
            }
            throw new Error(response.message || 'Failed to upload CSV');
        } catch (error) {
            console.error('Error uploading CSV:', error);
            this.setUploadResult({
                success: false,
                error: error.message,
            });
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Confirm CSV upload with reviewed user matches
     */
    confirmCSV = async ({ raceInfo, results }) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi('/api/externalraceresults/csv/confirm', { method: 'POST' }, {
                raceInfo,
                results,
            });
            if (response.status === 201 || response.status === 200) {
                this.setUploadResult({
                    success: true,
                    confirmed: true,
                    data: response.data,
                    meta: response.meta,
                });
                return response;
            }
            throw new Error(response.message || 'Failed to confirm CSV upload');
        } catch (error) {
            console.error('Error confirming CSV:', error);
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Update a specific result's user ID
     * @param {number} raceId - The ID of the external race result
     * @param {number} resultIndex - The index of the result in the results array
     * @param {number} newUserId - The new user ID to assign
     */
    updateResultUserId = async (raceId, resultIndex, newUserId) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi(`/api/externalraceresults/${raceId}`, { method: 'PATCH' }, {
                updateResultIndex: resultIndex,
                updateResultUserId: newUserId,
            });
            if (response.status === 200) {
                return response.data;
            }
            throw new Error(response.message || 'Failed to update result user ID');
        } catch (error) {
            console.error('Error updating result user ID:', error);
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Process external race result for ratings
     */
    processForRatings = async (id) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi(`/api/externalraceresults/${id}/process`, { method: 'POST' });
            if (response.status === 200) {
                return response.data;
            }
            throw new Error(response.message || 'Failed to process for ratings');
        } catch (error) {
            console.error('Error processing for ratings:', error);
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }
}

const store = new ExternalRaceResultStore();
export default store;
